import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Video, Mic, Wand2, Sparkles, Zap, Play, ArrowRight, Check, ChevronDown, Pause, Star, Flame, Rocket, LogIn } from 'lucide-react'

import logoIcon from '@/assets/icons/vs_64.png'

const LandingPage: React.FC = () => {
  const navigate = useNavigate()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isVideoLoaded, setIsVideoLoaded] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [playingVideos, setPlayingVideos] = useState<Set<number>>(new Set())

  // SEO: Update meta tags
  useEffect(() => {
    document.title = 'VisSynth - AI-Powered Video Creation Platform | Create Stunning Videos in Minutes'

    const updateOrCreateMeta = (attrName: string, attrValue: string, content: string) => {
      let meta = document.querySelector(`meta[${attrName}="${attrValue}"]`)
      if (!meta) {
        meta = document.createElement('meta')
        meta.setAttribute(attrName, attrValue)
        document.head.appendChild(meta)
      }
      meta.setAttribute('content', content)
    }

    updateOrCreateMeta('name', 'description', 'Create professional AI-generated videos for TikTok, YouTube Shorts, and social media. No camera, no editing skills required. Transform your ideas into viral videos in minutes with VisSynth.')
    updateOrCreateMeta('name', 'keywords', 'AI video generator, video creation, TikTok video maker, YouTube Shorts, automated video production, text to video, AI video editor')
    updateOrCreateMeta('property', 'og:title', 'VisSynth - AI-Powered Video Creation Platform')
    updateOrCreateMeta('property', 'og:description', 'Create stunning AI-generated videos in minutes. No camera or editing skills required.')
    updateOrCreateMeta('property', 'og:type', 'website')
    updateOrCreateMeta('property', 'og:url', 'https://vissynth.com')
    updateOrCreateMeta('property', 'og:image', 'https://vissynth.com/og-image.svg')
    updateOrCreateMeta('property', 'og:image:width', '1200')
    updateOrCreateMeta('property', 'og:image:height', '630')
    updateOrCreateMeta('property', 'og:image:alt', 'VisSynth AI Video Generation Platform')
    updateOrCreateMeta('property', 'og:image:type', 'image/svg+xml')
    updateOrCreateMeta('name', 'twitter:card', 'summary_large_image')
    updateOrCreateMeta('name', 'twitter:title', 'VisSynth - AI-Powered Video Creation Platform')
    updateOrCreateMeta('name', 'twitter:description', 'Create stunning AI-generated videos in minutes.')
    updateOrCreateMeta('name', 'twitter:image', 'https://vissynth.com/og-image.svg')

    let canonical = document.querySelector('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.setAttribute('rel', 'canonical')
      document.head.appendChild(canonical)
    }
    canonical.setAttribute('href', 'https://vissynth.com/')

    updateOrCreateMeta('name', 'author', 'VisSynth')
    updateOrCreateMeta('name', 'robots', 'index, follow')

    // Additional SEO tags
    updateOrCreateMeta('name', 'mobile-web-app-capable', 'yes')
    updateOrCreateMeta('name', 'apple-mobile-web-app-capable', 'yes')
    updateOrCreateMeta('name', 'apple-mobile-web-app-status-bar-style', 'black-translucent')
    updateOrCreateMeta('name', 'application-name', 'VisSynth')

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'VisSynth',
      url: 'https://vissynth.com',
      applicationCategory: 'MultimediaApplication',
      operatingSystem: 'Web',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.8',
        ratingCount: '1200'
      },
      description: 'AI-powered video creation platform for social media content creators',
      keywords: 'AI video generator, video creation, TikTok video maker, YouTube Shorts, automated video production',
      browserRequirements: 'Requires JavaScript. Requires HTML5.',
      softwareVersion: '1.0',
      author: {
        '@type': 'Organization',
        name: 'VisSynth',
        url: 'https://vissynth.com'
      }
    }

    let schemaScript = document.getElementById('structured-data')
    if (!schemaScript) {
      schemaScript = document.createElement('script')
      schemaScript.setAttribute('id', 'structured-data')
      schemaScript.setAttribute('type', 'application/ld+json')
      document.head.appendChild(schemaScript)
    }
    schemaScript.textContent = JSON.stringify(schema)

    // VideoObject 结构化数据 - Hero 教程视频
    const heroVideoSchema = {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: 'VisSynth AI Video Generator Tutorial',
      description: 'Learn how to create stunning AI-generated videos with VisSynth. Transform text into professional videos in minutes.',
      thumbnailUrl: ['https://vissynth.com/tutorial-preview.jpg'],
      uploadDate: '2025-01-01',
      duration: 'PT1M30S',
      contentUrl: 'https://data.vissynth.com/video/MU7DySdnaqbnkUJx/oElh0yJ1yw9JUu1p/tutorial.mp4',
      embedUrl: 'https://vissynth.com/',
      publisher: {
        '@type': 'Organization',
        name: 'VisSynth',
        url: 'https://vissynth.com',
        logo: {
          '@type': 'ImageObject',
          url: 'https://vissynth.com/og-image.svg'
        }
      },
      interactionStatistic: {
        '@type': 'InteractionCounter',
        interactionType: { '@type': 'WatchAction' },
        userInteractionCount: 15000
      }
    }

    let heroVideoSchemaScript = document.getElementById('hero-video-structured-data')
    if (!heroVideoSchemaScript) {
      heroVideoSchemaScript = document.createElement('script')
      heroVideoSchemaScript.setAttribute('id', 'hero-video-structured-data')
      heroVideoSchemaScript.setAttribute('type', 'application/ld+json')
      document.head.appendChild(heroVideoSchemaScript)
    }
    heroVideoSchemaScript.textContent = JSON.stringify(heroVideoSchema)

    // FAQPage 结构化数据
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    }

    let faqSchemaScript = document.getElementById('faq-structured-data')
    if (!faqSchemaScript) {
      faqSchemaScript = document.createElement('script')
      faqSchemaScript.setAttribute('id', 'faq-structured-data')
      faqSchemaScript.setAttribute('type', 'application/ld+json')
      document.head.appendChild(faqSchemaScript)
    }
    faqSchemaScript.textContent = JSON.stringify(faqSchema)

    return () => {
      document.querySelectorAll('meta[name="description"], meta[name="keywords"], link[rel="canonical"], #structured-data, #hero-video-structured-data, #faq-structured-data, meta[property^="og:"], meta[name^="twitter:"]').forEach(el => el.remove())
    }
  }, [])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)

    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)

    return () => {
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
    }
  }, [])

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const toggleVideo = () => {
    const video = videoRef.current
    if (video) {
      if (video.paused) {
        video.play()
      } else {
        video.pause()
      }
    }
  }

  const features = [
    {
      icon: Video,
      title: 'AI Video Generation',
      description: 'Transform text prompts into stunning videos with AI-powered image generation',
      gradient: 'from-cyan-500 via-blue-500 to-purple-500',
      glow: 'shadow-cyan-500/50'
    },
    {
      icon: Mic,
      title: 'AI Voiceover',
      description: 'Natural-sounding voice narration with advanced TTS technology',
      gradient: 'from-fuchsia-500 via-purple-500 to-pink-500',
      glow: 'shadow-fuchsia-500/50'
    },
    {
      icon: Wand2,
      title: 'Smart Storyboard',
      description: 'AI generates engaging storyboards and transitions automatically',
      gradient: 'from-pink-500 via-rose-500 to-red-500',
      glow: 'shadow-pink-500/50'
    },
    {
      icon: Sparkles,
      title: 'Image to Video',
      description: 'Convert static images into dynamic videos with animations',
      gradient: 'from-violet-500 via-purple-500 to-indigo-500',
      glow: 'shadow-violet-500/50'
    },
    {
      icon: Zap,
      title: 'Fast Rendering',
      description: 'Generate professional videos in minutes with cloud rendering',
      gradient: 'from-amber-500 via-orange-500 to-red-500',
      glow: 'shadow-amber-500/50'
    },
    {
      icon: Play,
      title: 'One-Click Export',
      description: 'Export videos optimized for all social platforms',
      gradient: 'from-emerald-500 via-teal-500 to-cyan-500',
      glow: 'shadow-emerald-500/50'
    }
  ]

  const exampleVideos = [
    {
      title: 'Solar System Exploration',
      category: 'Education',
      videoUrl: 'https://data.vissynth.com/video/QCPxqOd9C4BJm4N1/6sjTOmJxI6FDQ2Xf/video_task_1766655496917252748_U29sYXIgU3lzdGVtIEV4cGxvcmF0aW9uIFN0b3J5Ym9hcmQ=_1766655596.mp4',
      posterUrl: '/example-video-1.png',
      gradient: 'from-blue-600 via-cyan-500 to-teal-400'
    },
    {
      title: 'Harmony Smart Home',
      category: 'Technology',
      videoUrl: 'https://data.vissynth.com/video/RNuCN9aupLICDvAo/AMLZYoP1Ca1XsMzJ/video_task_1766657185809179921_SGFybW9ueSBTbWFydCBIb21lIEV4cGVyaWVuY2UgU3Rvcnlib2FyZA==_1766657274.mp4',
      posterUrl: '/example-video-2.png',
      gradient: 'from-purple-600 via-pink-500 to-rose-400'
    },
    {
      title: 'Enchanted Forest',
      category: 'Nature',
      videoUrl: 'https://data.vissynth.com/video/DjCcLNoeBPKE9XSg/Ogh5g63HGupdiCxZ/video_task_1766656134557276202_RW5jaGFudGVkIEZvcmVzdCBTeW1waG9ueSBTdG9yeWJvYXJk_1766656257.mp4',
      posterUrl: '/example-video-3.png',
      gradient: 'from-emerald-600 via-green-500 to-lime-400'
    },
    {
      title: 'City Superhero Delivery',
      category: 'Entertainment',
      videoUrl: 'https://data.vissynth.com/video/WGJTm09biKR54K1z/dHSKdHyw99OWzD4w/video_task_1766656957552081268_Q2l0eSBTdXBlcmhlcm8gRGVsaXZlcnkgU2VydmljZSBTdG9yeWJvYXJk_1766657061.mp4',
      posterUrl: '/example-video-4.png',
      gradient: 'from-amber-600 via-orange-500 to-yellow-400'
    }
  ]

  const steps = [
    {
      number: 1,
      title: 'Describe Your Idea',
      description: 'Enter your video topic, theme, and let AI know your vision',
      color: 'blue'
    },
    {
      number: 2,
      title: 'AI Generation',
      description: 'AI creates script, generates images, and produces voiceover narration',
      color: 'purple'
    },
    {
      number: 3,
      title: 'Customize Scenes',
      description: 'Fine-tune prompts, adjust transitions, and perfect video timing',
      color: 'pink'
    },
    {
      number: 4,
      title: 'Export & Share',
      description: 'Download your video and share directly to social media platforms',
      color: 'cyan'
    }
  ]

  const stats = [
    { value: '10K+', label: 'Videos Created', gradient: 'from-blue-500 to-cyan-500' },
    { value: '1000+', label: 'Active Users', gradient: 'from-purple-500 to-pink-500' },
    { value: '4.8★', label: 'User Rating', gradient: 'from-amber-500 to-orange-500' },
    { value: '60s', label: 'Avg. Time', gradient: 'from-emerald-500 to-teal-500' }
  ]

  const useCases = [
    {
      icon: Video,
      title: 'Content Creators',
      description: 'Create viral videos daily without editing',
      gradient: 'from-blue-600 to-cyan-500',
      glow: 'shadow-blue-500/50'
    },
    {
      icon: Sparkles,
      title: 'Marketers',
      description: 'Scale your video content production',
      gradient: 'from-purple-600 to-pink-500',
      glow: 'shadow-purple-500/50'
    },
    {
      icon: Rocket,
      title: 'Businesses',
      description: 'Professional videos for social media',
      gradient: 'from-pink-600 to-rose-500',
      glow: 'shadow-pink-500/50'
    }
  ]

  const faqs = [
    {
      question: 'How does AI video generation work?',
      answer: 'VisSynth uses advanced AI models for scripts, images, and specialized video generation models. Simply describe your vision, and our AI handles everything.'
    },
    {
      question: 'Do I need video editing experience?',
      answer: 'Not at all! VisSynth is designed for everyone. Our AI automates complex video editing tasks. No technical skills or camera equipment required.'
    },
    {
      question: 'What video formats are supported?',
      answer: 'We export videos optimized for all major platforms: TikTok (9:16), YouTube Shorts (9:16), Instagram Reels (9:16), and standard formats (16:9). Export in MP4 format.'
    },
    {
      question: 'Can I use the videos commercially?',
      answer: 'Yes! All videos you create with VisSynth are yours to use commercially. Perfect for content creators, marketers, and businesses.'
    },
    {
      question: 'How long does it take to generate a video?',
      answer: 'Most videos are generated in 2-5 minutes, depending on length and complexity. Short videos (15-30 seconds) can be ready in under 2 minutes.'
    },
    {
      question: 'Is there a free trial?',
      answer: 'Yes! Get started with our free plan to explore all features. Upgrade to Pro for unlimited generations and advanced features.'
    }
  ]

  const [activeFaq, setActiveFaq] = useState<number | null>(null)

  const toggleFaq = (index: number) => {
    setActiveFaq(activeFaq === index ? null : index)
  }

  return (
    <div className="bg-gradient-to-b from-gray-950 via-slate-950 via-gray-900 via-slate-900 to-gray-950 text-white min-h-screen overflow-x-hidden">
      {/* Floating Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-2xl border-b border-white/5 transition-all duration-300 ${isScrolled
        ? 'bg-gray-950/50 py-2 shadow-lg'
        : 'bg-gray-950/10 py-4'
        }`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between">
            {/* Left: Logo and Brand */}
            <div className="flex items-center space-x-1 cursor-pointer" onClick={() => navigate('/')}>
              <img src={logoIcon} alt="V" className="w-8 h-8 self-center" />
              <span className="text-xl font-black text-white self-center mt-0.5">
                VisSynth
              </span>
            </div>

            {/* Center: Navigation Links */}
            <nav className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => navigate('/pricing')}
                className="text-gray-300 hover:text-white transition-colors font-medium text-sm"
              >
                Pricing
              </button>
              <button
                onClick={() => navigate('/feedback')}
                className="text-gray-300 hover:text-white transition-colors font-medium text-sm"
              >
                Help
              </button>
            </nav>

            {/* Right: Login and Start Creating */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/login')}
                className="hidden sm:flex items-center space-x-2 px-4 py-2 text-gray-300 hover:text-white transition-colors font-medium text-sm"
              >
                <LogIn className="w-4 h-4" />
                <span>Login</span>
              </button>
              <button
                onClick={() => navigate('/production/video/input')}
                className="px-5 py-2 bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600 rounded-lg font-bold text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 text-sm"
              >
                Start Creating
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-16"></div>
      {/* Animated Background Particles */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="particle absolute w-1 h-1 bg-white/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 15}s`,
              animationDuration: `${15 + Math.random() * 10}s`
            }}
          />
        ))}
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Animated gradient background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-r from-cyan-500/30 via-blue-500/30 to-purple-500/30 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-r from-purple-500/30 via-pink-500/30 to-rose-500/30 rounded-full blur-3xl animate-float-delayed"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-pink-600/20 rounded-full blur-3xl animate-pulse"></div>
        </div>

        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px]"></div>

        <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-32 mb-12">
          <div className="grid lg:grid-cols-[1fr,1.1fr] gap-8 items-center">
            {/* Left: Content */}
            <div className="space-y-8 animate-slide-in-up">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-md overflow-hidden relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <Sparkles className="w-4 h-4 text-cyan-400 relative z-10" />
                <span className="text-sm text-cyan-300 font-medium relative z-10">✨ AI-Powered Video Platform</span>
              </div>

              {/* Main Heading */}
              <h1 className="text-5xl lg:text-7xl font-bold leading-tight">
                <span className="block mb-2">Create Viral Videos</span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 animate-gradient-flow font-black">
                  With AI Magic
                </span>
              </h1>

              {/* Subheading */}
              <p className="text-xl text-gray-300 leading-relaxed max-w-xl">
                Transform your ideas into stunning videos in seconds. No camera, no editing skills required.
                <span className="block mt-2 text-cyan-400 font-semibold">Perfect for TikTok, YouTube & Instagram</span>
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/production/video/input')}
                  className="group relative px-8 py-4 bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600 rounded-lg font-bold text-lg neon-glow text-white overflow-hidden transition-all duration-300 hover:scale-105 animate-gradient-flow"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    Start Creating Free
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                  </span>
                </button>
                <button
                  onClick={() => document.getElementById('example-videos')?.scrollIntoView({ behavior: 'smooth' })}
                  className="px-8 py-4 border-2 border-purple-500/50 hover:border-purple-400 rounded-lg font-semibold transition-all duration-300 hover:bg-purple-500/10 flex items-center justify-center gap-2 backdrop-blur-sm"
                >
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap gap-6 text-sm text-gray-400">
                {['150 credits free', 'Cancel anytime'].map((badge, i) => (
                  <div key={i} className="flex items-center gap-2 animate-bounce-in" style={{ animationDelay: `${i * 0.1}s` }}>
                    <Check className="w-4 h-4 text-emerald-400" />
                    <span>{badge}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Video Demo */}
            <div className="relative animate-slide-in-up" id="demo-video">
              {/* Tech glass card with strong shadow */}
              <div className="relative backdrop-blur-xl bg-gray-900/60 rounded-2xl p-4 border border-gray-700/50 shadow-2xl shadow-black/80 transform hover:scale-105 transition-transform duration-500 hover:shadow-2xl hover:shadow-black/90">
                {/* Video wrapper - exact video ratio 1651:993 */}
                <div className="rounded-xl overflow-hidden bg-gray-900/50 relative group backdrop-blur-sm shadow-inner" style={{ aspectRatio: '1651 / 993' }}>
                  <video
                    ref={videoRef}
                    className="w-full h-full object-cover"
                    poster="/tutorial-preview.jpg"
                    src="https://data.vissynth.com/video/MU7DySdnaqbnkUJx/oElh0yJ1yw9JUu1p/tutorial.mp4"
                    playsInline
                    loop
                    preload="metadata"
                    onLoadedData={() => setIsVideoLoaded(true)}
                  />

                  {/* Loading overlay with VisSynth text */}
                  {!isVideoLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-900/90 backdrop-blur-sm">
                      <div className="text-center">
                        <div className="text-3xl lg:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 animate-pulse">
                          VisSynth
                        </div>
                        <div className="mt-3 flex justify-center gap-1">
                          <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Play overlay */}
                  {isVideoLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 backdrop-blur-sm">
                      <button
                        onClick={toggleVideo}
                        className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center transform hover:scale-110 transition-all duration-300 shadow-xl hover:shadow-blue-500/50"
                      >
                        {isPlaying ? <Pause className="w-8 h-8 text-white" /> : <Play className="w-8 h-8 text-white ml-1" />}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-20">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="animate-count-up glass-card rounded-xl p-6 text-center relative overflow-hidden group hover:scale-105 transition-all duration-300"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                <div className={`text-4xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r ${stat.gradient} mb-2`}>
                  {stat.value}
                </div>
                <div className="text-sm text-gray-400 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 hidden lg:block">
            <ChevronDown className="w-6 h-6 text-cyan-400 animate-bounce" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-md mb-6">
              <Flame className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-purple-300 font-medium">Powerful Features</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-black mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600">
                Everything You Need
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Cutting-edge AI features for professional video creation
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group relative p-8 rounded-2xl border border-gray-700/50 hover:border-gray-600 transition-all duration-500 hover:scale-105 overflow-hidden animate-slide-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Gradient background on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>

                {/* Icon */}
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 relative overflow-hidden`}>
                  <div className="absolute inset-0 bg-black/20"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
                  <feature.icon className="w-8 h-8 text-white relative z-10" />
                  <div className={`absolute inset-0 shadow-${feature.glow.split('-')[1]}-500/50 blur-md`}></div>
                </div>

                <h3 className="text-2xl font-bold mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-cyan-400 group-hover:to-purple-400 transition-all">
                  {feature.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Example Videos Section */}
      <section id="example-videos" className="py-16 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-purple-600/15 to-pink-600/15 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-br from-blue-600/15 to-cyan-600/15 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-md mb-6">
              <Sparkles className="w-4 h-4 text-pink-400 animate-twinkle" />
              <span className="text-sm text-pink-300 font-medium">Example Videos</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-black mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-purple-500 to-indigo-500">
                See What You Can Create
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Explore AI-generated videos across different categories
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {exampleVideos.map((example, index) => (
              <div
                key={index}
                className="group relative aspect-[9/16] rounded-2xl overflow-hidden cursor-pointer transform hover:scale-105 transition-all duration-500 animate-slide-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Video */}
                <video
                  src={example.videoUrl}
                  poster={example.posterUrl}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loop
                  playsInline
                  preload="metadata"
                  aria-label={`${example.title} - ${example.category} video demo`}
                  onMouseEnter={(e) => {
                    const video = e.currentTarget;
                    video.play().catch(err => {
                      console.log('Video play error:', err);
                    });
                  }}
                  onMouseLeave={(e) => e.currentTarget.pause()}
                  onPlay={() => {
                    setPlayingVideos(prev => new Set(prev).add(index));
                  }}
                  onPause={() => {
                    setPlayingVideos(prev => {
                      const newSet = new Set(prev);
                      newSet.delete(index);
                      return newSet;
                    });
                  }}
                />

                {/* Gradient overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${example.gradient} opacity-0 group-hover:opacity-30 transition-opacity duration-500 pointer-events-none`}></div>

                {/* Bottom overlay - always visible */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent flex flex-col items-end justify-end p-4 pointer-events-none">
                  <h3 className="text-sm font-bold text-white text-center">{example.title}</h3>
                </div>

                {/* Category badge */}
                <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-black/40 backdrop-blur-md border border-white/10 text-xs font-medium pointer-events-none">
                  {example.category}
                </div>

                {/* Play icon on hover - only show when paused */}
                <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 bg-black/40 pointer-events-none ${playingVideos.has(index) ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'}`}>
                  <Play className="w-16 h-16 text-white" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-md mb-6">
              <Rocket className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-emerald-300 font-medium">Simple Process</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-black mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-500 to-cyan-500">
                Create in 4 Simple Steps
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              From idea to finished video in minutes
            </p>
          </div>

          {/* Horizontal 4 Steps Layout */}
          <div className="relative">
            {/* Continuous line connecting all steps */}
            <div className="hidden lg:block absolute top-8 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500/50 via-purple-500/50 via-pink-500/50 to-cyan-500/50"></div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
              {steps.map((step, index) => (
                <div key={index} className="relative text-center">
                  {/* Step Card */}
                  <div className="relative group">
                    {/* Number Badge - positioned on the line */}
                    <div className={`relative z-10 w-16 h-16 rounded-2xl bg-gray-900 border-2 flex items-center justify-center font-bold text-2xl mb-4 group-hover:scale-110 transition-transform duration-300 mx-auto shadow-lg ${step.color === 'blue' ? 'border-blue-500/50 text-blue-400' :
                      step.color === 'purple' ? 'border-purple-500/50 text-purple-400' :
                        step.color === 'pink' ? 'border-pink-500/50 text-pink-400' :
                          'border-cyan-500/50 text-cyan-400'
                      }`}>
                      {step.number}
                    </div>

                    {/* Content */}
                    <div>
                      <h3 className={`text-2xl font-bold mb-3 ${step.color === 'blue' ? 'text-blue-400' :
                        step.color === 'purple' ? 'text-purple-400' :
                          step.color === 'pink' ? 'text-pink-400' :
                            'text-cyan-400'
                        }`}>{step.title}</h3>
                      <p className="text-gray-400 text-base leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-16">
            <button
              onClick={() => navigate('/production/video/input')}
              className="px-12 py-5 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-lg font-bold text-xl hover:shadow-2xl hover:shadow-emerald-500/30 transition-all duration-300 transform hover:scale-105 animate-gradient-flow text-white"
            >
              Start Creating Now
            </button>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-500 to-rose-500">
                Perfect for Every Creator
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Whether you're a beginner or a pro, VisSynth delivers
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <div
                key={index}
                className="group glass-card rounded-2xl p-8 text-center hover:scale-105 transition-all duration-500 animate-slide-in-up relative overflow-hidden"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Gradient overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${useCase.gradient} opacity-0 group-hover:opacity-20 transition-opacity duration-500`}></div>

                <div className="relative">
                  <div className={`w-24 h-24 rounded-full bg-gradient-to-br ${useCase.gradient} flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-500 shadow-xl relative overflow-hidden`}>
                    <div className="absolute inset-0 bg-black/20"></div>
                    <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
                    <useCase.icon className="w-12 h-12 text-white relative z-10" />
                  </div>
                  <h3 className="text-2xl font-black mb-3">{useCase.title}</h3>
                  <p className="text-gray-400">{useCase.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 relative">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-orange-500 to-red-500">
                Frequently Asked Questions
              </span>
            </h2>
            <p className="text-xl text-gray-400">Everything you need to know</p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="glass-card rounded-xl overflow-hidden hover:scale-[1.02] transition-all duration-300"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-white/5 transition-colors"
                >
                  <span className="font-bold text-lg">{faq.question}</span>
                  <ChevronDown
                    className={`w-6 h-6 transition-transform duration-300 ${activeFaq === index ? 'rotate-180' : ''}`}
                  />
                </button>
                {activeFaq === index && (
                  <div className="px-6 pb-5 animate-slide-in-up">
                    <p className="text-gray-400 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-cyan-600/30 via-purple-600/30 to-pink-600/30 rounded-full blur-3xl animate-pulse"></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="glass-card rounded-3xl p-12 border-2 border-cyan-500/20 relative overflow-hidden">
            {/* Animated gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-pink-500/10 animate-gradient-flow"></div>

            <div className="relative">
              <h2 className="text-4xl lg:text-6xl font-black mb-6">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 animate-gradient-flow">
                  Ready to Create?
                </span>
              </h2>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Join thousands of creators already using VisSynth
              </p>
              <button
                onClick={() => navigate('/production/video/input')}
                className="px-16 py-6 bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600 rounded-lg font-black text-xl hover:shadow-2xl hover:shadow-cyan-500/40 transition-all duration-300 transform hover:scale-105 animate-gradient-flow text-white relative overflow-hidden"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  Get Started Free
                  <Rocket className="w-6 h-6" />
                </span>
              </button>
              <p className="mt-6 text-sm text-gray-400">
                No credit card • Free forever • Cancel anytime
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-12 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center text-gray-400">
            <p className="mb-4">&copy; 2025 VisSynth. All rights reserved.</p>
            <div className="flex justify-center gap-6 text-sm">
              <a href="/privacy" className="hover:text-cyan-400 transition-colors">Privacy</a>
              <a href="/terms" className="hover:text-cyan-400 transition-colors">Terms</a>
              <a href="/feedback" className="hover:text-cyan-400 transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default LandingPage
